import React, { useEffect, useState, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Divider, 
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  MonetizationOn as CoinIcon,
  CheckCircle as SuccessIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import AppLayout from '../components/layout/AppLayout';
import { getCoinPackages, getCoinTransactions, purchaseCoins } from '../services/coinService';
import { CoinPackage, CoinTransaction, TransactionType } from '../types/coin';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';

const CoinsPage: React.FC = () => {
  const [packages, setPackages] = useState<CoinPackage[]>([]);
  const [transactions, setTransactions] = useState<CoinTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const redirectHandled = useRef(false);
  const { userProfile, refreshUserProfile } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchData();
  }, []);

  // Separate useEffect for handling Stripe redirect parameters
  useEffect(() => {
    const isSuccess = searchParams.get('success');
    const isCancelled = searchParams.get('cancelled');
    
    // Only handle redirect parameters once
    if (!redirectHandled.current && (isSuccess === 'true' || isCancelled === 'true')) {
      redirectHandled.current = true;
      
      if (isSuccess === 'true') {
        setSuccess('Payment successful! Your coins have been added to your account.');
        // Refresh user profile to get updated coin balance
        refreshUserProfile();
      } else if (isCancelled === 'true') {
        setError('Payment was cancelled. Please try again if you wish to purchase coins.');
      }
      
      // Clear URL parameters after handling
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 100);
    }
  }, [searchParams, refreshUserProfile]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [packagesData, transactionsData] = await Promise.all([
        getCoinPackages(),
        getCoinTransactions()
      ]);
      setPackages(packagesData);
      setTransactions(transactionsData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load coin data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId: string) => {
    try {
      setPurchasing(true);
      setError('');
      setSuccess('');

      // This will redirect to Stripe checkout
      await purchaseCoins({ packageId });
      
    } catch (err: any) {
      console.error('Error creating checkout session:', err);
      setError('Failed to create checkout session. Please try again.');
      setPurchasing(false);
    }
  };

  const getTransactionTypeLabel = (type: TransactionType) => {
    switch (type) {
      case TransactionType.PURCHASE:
        return 'Purchase';
      case TransactionType.USAGE:
        return 'Used for Research';
      case TransactionType.REFUND:
        return 'Refund';
      case TransactionType.BONUS:
        return 'Bonus';
      default:
        return 'Unknown';
    }
  };

  const getTransactionTypeColor = (type: TransactionType) => {
    switch (type) {
      case TransactionType.PURCHASE:
        return 'primary';
      case TransactionType.USAGE:
        return 'error';
      case TransactionType.REFUND:
        return 'info';
      case TransactionType.BONUS:
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="h4" gutterBottom>
          Coin Management
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            icon={<CancelIcon />}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 3 }}
            icon={<SuccessIcon />}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}

        <Grid container spacing={4}>
          {/* Coin Balance */}
          <Grid sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Current Balance
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                <CoinIcon sx={{ fontSize: 40, color: '#FFD700', mr: 1 }} />
                <Typography variant="h3" fontWeight="bold">
                  {userProfile?.coinBalance || 0}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Use coins for deep research and car analysis. Each research session costs 1 coin.
              </Typography>
            </Paper>
          </Grid>

          {/* Coin Packages */}
          <Grid sx={{ gridColumn: { xs: 'span 12', md: 'span 8' } }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Purchase Coins
              </Typography>
              <Box 
                sx={{ 
                  mb: 2, 
                  p: 2, 
                  bgcolor: 'rgba(255, 193, 7, 0.1)',
                  borderRadius: 1,
                  border: '1px solid rgba(255, 193, 7, 0.3)'
                }}
              >
                <Typography variant="body2" fontWeight="bold" color="warning.main" sx={{ mb: 1 }}>
                  🎉 Limited Time Offer - First 1000 Users Only!
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  <Typography component="li" variant="body2" color="text.secondary">
                    💰 Save $9.99 on every package
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    🛡️ 30-day money back guarantee
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {packages.map((pkg) => (
                  <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }} key={pkg.id}>
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        p: 2, 
                        borderRadius: 2,
                        border: pkg.isPromotional ? '2px solid' : '1px solid',
                        borderColor: pkg.isPromotional ? 'secondary.main' : 'divider',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      {pkg.isPromotional && (
                        <Box 
                          sx={{ 
                            position: 'absolute', 
                            top: 10, 
                            right: -30, 
                            transform: 'rotate(45deg)',
                            bgcolor: 'secondary.main',
                            px: 4,
                            py: 0.5,
                          }}
                        >
                          <Typography variant="caption" fontWeight="bold">
                            BEST VALUE
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CoinIcon sx={{ color: '#FFD700', mr: 1 }} />
                        <Typography variant="h6">{pkg.name}</Typography>
                      </Box>
                      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                        {pkg.coins} coins
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        {/* Original Price (crossed out) */}
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            textDecoration: 'line-through',
                            mb: 0.5
                          }}
                        >
                          Originally ${(pkg.price + 9.99).toFixed(2)}
                        </Typography>
                        {/* Discounted Price */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h5" fontWeight="bold" color="success.main">
                            ${pkg.price.toFixed(2)}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              bgcolor: 'success.main',
                              color: 'white',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              fontWeight: 'bold'
                            }}
                          >
                            Save $9.99
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, height: 40 }}>
                        {pkg.description}
                      </Typography>
                      <Button 
                        variant={pkg.isPromotional ? "contained" : "outlined"} 
                        fullWidth
                        onClick={() => handlePurchase(pkg.id)}
                        disabled={purchasing}
                      >
                        {purchasing ? <CircularProgress size={24} /> : 'Buy Now'}
                      </Button>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Transaction History */}
          <Grid sx={{ gridColumn: 'span 12' }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Transaction History
              </Typography>
              {transactions.length > 0 ? (
                <TableContainer>
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right">Balance After</TableCell>
                        <TableCell>Related Session</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell>
                            {format(new Date(transaction.timestamp), 'MMM d, yyyy h:mm a')}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={getTransactionTypeLabel(transaction.type)} 
                              color={getTransactionTypeColor(transaction.type) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              fontWeight="bold"
                              color={transaction.type === TransactionType.USAGE ? 'error.main' : 'success.main'}
                            >
                              {transaction.type === TransactionType.USAGE ? '-' : '+'}{transaction.amount}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{transaction.balanceAfter}</TableCell>
                          <TableCell>
                            {transaction.relatedSessionId ? (
                              <Button 
                                variant="text" 
                                size="small" 
                                href={`/chat/${transaction.relatedSessionId}`}
                              >
                                View Chat
                              </Button>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No transactions found.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </AppLayout>
  );
};

export default CoinsPage;
